import { Property, SelectInput } from '@/components/form/fields/select/SelectInput';
import { Category, Manufacturer } from '@/services/api/contracts/catalog/types';
import { FinanceModel } from '@/services/api/contracts/financemodels';
import { useCategories, useManufacturers } from '@/services/api/hooks/catalog';
import { FunctionComponent, useEffect, useState } from 'react';
import { getModelTypeString } from '../bulk/helpers/utils';
import { DEFAULT_SORT_OPTION, FinanceModelFilterOptions, PERIPHERAL_CAT_FILTER_ID } from './filter/helper';

const buildCategoryFilterOptions = (categories: Category[], models: FinanceModel[]) => {
  const activeCategories = categories.filter(cat => models.some(model => model.categoryId === cat.id));

  return [
    DEFAULT_SORT_OPTION,
    ...activeCategories.map(cat => {
      return {
        id: cat.id,
        name: cat.name
      };
    }),
    {
      id: PERIPHERAL_CAT_FILTER_ID,
      name: 'Peripheral'
    }
  ];
};

const buildManufacturerFilterOptions = (manufacturers: Manufacturer[], models: FinanceModel[]) => {
  const activeManufacturers = manufacturers.filter(man => models.some(model => model.manufacturerId === man.id));

  return [
    DEFAULT_SORT_OPTION,
    ...activeManufacturers.map(cat => {
      return {
        id: cat.id,
        name: cat.name
      };
    })
  ];
};

const buildTypeFilterOptions = (models: FinanceModel[]) => {
  const types = models.map(model => model.financeModelType).filter(type => type);

  const uniqueTypes = Array.from(new Set(types));

  return [
    DEFAULT_SORT_OPTION,
    ...uniqueTypes.map(type => {
      return {
        id: type.toString(),
        name: getModelTypeString(type)
      };
    })
  ];
};

const buildTermFilterOptions = (models: FinanceModel[]) => {
  const terms = models.map(model => model.term).filter(term => term);

  const uniqueTerms = Array.from(new Set(terms));

  return [
    DEFAULT_SORT_OPTION,
    ...uniqueTerms.map(term => {
      return {
        id: String(term),
        name: `${term}yr`
      };
    })
  ];
};
interface FinanceModelFiltersProps {
  models: FinanceModel[];
  onChange: (options: FinanceModelFilterOptions) => void;
}

const FinanceModelFilters: FunctionComponent<FinanceModelFiltersProps> = ({ models, onChange }) => {
  const [categories] = useCategories();
  const categoryOptions = buildCategoryFilterOptions(categories, models);

  const [manufacturers] = useManufacturers();
  const manufacturerOptions = buildManufacturerFilterOptions(manufacturers, models);

  const [categoryFilter, setCategoryFilter] = useState<Property | null>(null);
  const [manufacturerFilter, setManufacturerFilter] = useState<Property | null>(null);
  const [typeFilter, setTypeFilter] = useState<Property | null>(null);
  const [termFilter, setTermFilter] = useState<Property | null>(null);

  useEffect(() => {
    onChange({
      category: categoryFilter,
      manufacturer: manufacturerFilter,
      type: typeFilter,
      term: termFilter
    });
  }, [categoryFilter, manufacturerFilter, typeFilter, termFilter]);

  return (
    <>
      <SelectInput
        className="w-[200px]"
        id={'categoryFilter'}
        value={categoryFilter}
        onChange={(value: Property | null) => {
          setCategoryFilter(value);
        }}
        options={categoryOptions}
        placeholder="Category"
      />
      <SelectInput
        className="w-[200px]"
        id={'manufacturerFilter'}
        value={manufacturerFilter}
        onChange={(value: Property | null) => {
          setManufacturerFilter(value);
        }}
        options={manufacturerOptions}
        placeholder="Manufacturer"
      />
      <SelectInput
        className="w-[200px]"
        id={'typeFilter'}
        value={typeFilter}
        onChange={(value: Property | null) => {
          setTypeFilter(value);
        }}
        options={buildTypeFilterOptions(models)}
        placeholder="Type"
      />
      <SelectInput
        className="w-[200px]"
        id={'termFilter'}
        value={termFilter}
        onChange={(value: Property | null) => {
          setTermFilter(value);
        }}
        options={buildTermFilterOptions(models)}
        placeholder="Term"
      />
    </>
  );
};

export default FinanceModelFilters;
